import React, { useState, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, FlagIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button } from "components/ui";
import { ConfirmModal } from "components/shared/ConfirmModal";
import { getPriorityList, setLeadPriority } from "services/leadsService";

const SetPriorityModal = ({
  isOpen,
  onClose,
  leadData,
  handlePriorityChange,
}) => {
  const [selectedPriority, setSelectedPriority] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [priorities, setPriorities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmState, setConfirmState] = useState("pending");
  const [confirmMessages, setConfirmMessages] = useState({});

  const saveRef = useRef(null);

  // Fetch priority list when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchPriorities();
    } else {
      // Reset confirmation modal state when main modal closes
      setShowConfirmModal(false);
      setConfirmState("pending");
      setIsSubmitting(false);
    }
  }, [isOpen]);
  // Set initial priority based on lead data, defaulting to "High"
  useEffect(() => {
    if (isOpen && priorities.length > 0) {
      // 1. Try to find the lead's current priority from leadData
      const currentPriorityName = leadData?.priority || leadData?.Priority;
      
      if (currentPriorityName && currentPriorityName !== "N/A") {
        const currentPriority = priorities.find(
          (p) => p.PriorityName.toLowerCase() === currentPriorityName.toLowerCase()
        );
        if (currentPriority) {
          setSelectedPriority(currentPriority.ID || currentPriority.Id);
          return;
        }
      }

      // 2. Default to "High" if no specific priority found in leadData
      const highPriority = priorities.find(
        (p) => (p.PriorityName || "").toLowerCase() === "high"
      );
      if (highPriority) {
        setSelectedPriority(highPriority.ID || highPriority.Id);
      } else {
        setSelectedPriority(priorities[0]?.ID || priorities[0]?.Id);
      }
    }
  }, [isOpen, priorities, leadData]);

  const fetchPriorities = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPriorityList();
      if (result.success) {
        setPriorities(result.data);
      } else {
        setError(result.error || "Failed to load priorities");
      }
    } catch (err) {
      setError("Failed to load priorities");
      console.error("Error fetching priorities:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const getLeadDisplayName = (lead) => {
    const isInvalid = (val) =>
      !val ||
      typeof val !== "string" ||
      ["unknown", "unknown lead", "unknown user", "n/a", "-", "null", "undefined"].includes(val.trim().toLowerCase());

    if (lead?.title && !isInvalid(lead.title)) return lead.title.trim();
    if (lead?.Title && !isInvalid(lead.Title)) return lead.Title.trim();
    if (lead?.leadTitle && !isInvalid(lead.leadTitle)) return lead.leadTitle.trim();
    if (lead?.name && !isInvalid(lead.name)) return lead.name.trim();
    if (lead?.FullName && !isInvalid(lead.FullName)) return lead.FullName.trim();
    if (lead?.fullName && !isInvalid(lead.fullName)) return lead.fullName.trim();
    if (lead?.clientContact && !isInvalid(lead.clientContact)) return lead.clientContact.trim();
    if (lead?.LeadName && !isInvalid(lead.LeadName)) return lead.LeadName.trim();

    const rawTitle = lead?.LeadRawJsonData?.find(
      (item) => item?.MasterFieldName?.toLowerCase() === "title"
    )?.Value;
    if (rawTitle && !isInvalid(rawTitle)) return rawTitle.trim();

    return "this lead";
  };

  const handleSubmit = () => {
    if (!selectedPriority) return;

    const selectedPriorityData = priorities.find(
      (p) => (p.Id || p.ID) === selectedPriority
    );

    const displayName = getLeadDisplayName(leadData);

    setConfirmMessages({
      pending: {
        title: "Confirm Priority Change",
        description: `Are you sure you want to change the priority of ${displayName} to ${selectedPriorityData?.PriorityName || "this priority"}?`,
        actionText: "Confirm",
      },
      success: {
        title: "Priority Updated",
        description: "The lead priority has been successfully updated.",
        actionText: "Done",
      },
      error: {
        title: "Update Failed",
        description: "Failed to update priority. Please try again.",
        actionText: "Retry",
      },
    });

    setConfirmState("pending");
    setShowConfirmModal(true);
  };

  const handleConfirmSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Use the new setLeadPriority service
      const result = await setLeadPriority(leadData?.id, selectedPriority);
      if (result?.success) {
        toast.success("Priority Updated", {
          description: "The lead priority has been successfully updated.",
        });
        handlePriorityChange();
        setShowConfirmModal(false);
        onClose();
      } else {
        setConfirmState("error");
        setError(result.error || "Failed to set priority");
      }
    } catch (error) {
      console.error("Error setting priority:", error);
      setConfirmState("error");
      setError("Failed to set priority. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    if (confirmState === "success") {
      onClose();
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => { }}
        initialFocus={saveRef}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="dark:bg-dark-700 relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 outline-none focus:outline-none">
            {/* Header */}
            <div className="dark:bg-dark-800 flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 sm:px-5">
              <DialogTitle
                as="h3"
                className="dark:text-dark-100 flex items-center gap-2 text-base font-medium text-gray-800"
              >
                <FlagIcon className="h-5 w-5 text-orange-600" />
                Set Priority
              </DialogTitle>
              <Button
                onClick={onClose}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            {/* Body (scrollable) */}
            <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
              {error && (
                <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
                  {error}
                </div>
              )}

              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                Set the priority level for {getLeadDisplayName(leadData)} to
                help organize and prioritize follow-ups.
              </p>

              {isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                    Loading priorities...
                  </span>
                </div>
              ) : priorities.length === 0 ? (
                <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No priority levels available
                </div>
              ) : (
                <div className="space-y-3">
                  {priorities.map((priority) => (
                    <label
                      key={priority.Id}
                      className="flex cursor-pointer items-center gap-3 rounded-lg border border-gray-200 p-3 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700"
                    >
                      <input
                        type="radio"
                        name="priority"
                        value={priority.ID}
                        checked={selectedPriority === priority.ID}
                        onChange={(e) =>
                          setSelectedPriority(parseInt(e.target.value))
                        }
                        className="text-blue-600 focus:ring-blue-500"
                      />
                      <div className="flex-1">
                        <span className={`text-sm font-medium`}>
                          {priority.PriorityName}
                        </span>
                        {priority.Description && (
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {priority.Description}
                          </p>
                        )}
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Footer (fixed) */}
            <div className="dark:bg-dark-700 flex justify-end gap-3 border-t border-gray-200 bg-white px-4 py-3 sm:px-5 dark:border-gray-700">
              <Button
                onClick={onClose}
                variant="outlined"
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting || isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                color="primary"
                ref={saveRef}
                className="min-w-[7rem] rounded-full"
                disabled={isSubmitting || isLoading || !selectedPriority}
              >
                {isSubmitting ? "Setting..." : "Set Priority"}
              </Button>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>

      {/* Confirmation Modal */}
      <ConfirmModal
        show={showConfirmModal}
        onClose={handleConfirmClose}
        onOk={
          confirmState === "success" ? handleConfirmClose : handleConfirmSubmit
        }
        confirmLoading={isSubmitting}
        state={confirmState}
        messages={confirmMessages}
        buttonColor="primary"
      />
    </Transition>
  );
};

export default SetPriorityModal;
